import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowStage } from '@/utils/AuditTrail/types';
import { STAGE_STATUS_CONFIG } from '@/utils/AuditTrail/constants';

export const WorkflowStages: React.FC<{ stages: WorkflowStage[] }> = ({ stages }) => {
  if (stages.length === 0) return null;

  return (
    <Accordion type="multiple" className="space-y-2">
      {stages.map((stage) => {
        const statusCfg = STAGE_STATUS_CONFIG[stage.status];
        return (
          <AccordionItem key={stage.id} value={stage.id}>
            <AccordionTrigger className="px-4">
              <div className="flex items-center gap-2 flex-wrap text-left">
                <span className="font-medium text-sm">{stage.stageName}</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{stage.approvalType}</span>
                <Badge className={cn('text-xs ml-1', statusCfg.className)}>{statusCfg.label}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-4">
              {/* Skipped reason */}
              {stage.status === 'skipped' && stage.skippedReason && (
                <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{stage.skippedReason}</p>
                </div>
              )}

              {/* Rule Evaluation Log */}
              {stage.ruleEvaluations && stage.ruleEvaluations.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Rule Evaluation Log
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-1.5 text-xs h-auto">Rule Name</TableHead>
                        <TableHead className="py-1.5 text-xs h-auto">Condition Met</TableHead>
                        <TableHead className="py-1.5 text-xs h-auto">Action Taken</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stage.ruleEvaluations.map((rule, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-1.5 text-xs">{rule.ruleName}</TableCell>
                          <TableCell className="py-1.5 text-xs">
                            <Badge
                              className={cn(
                                'text-xs',
                                rule.conditionMet
                                  ? 'bg-success-100 text-success-800'
                                  : 'bg-neutral-100 text-neutral-700',
                              )}
                            >
                              {rule.conditionMet ? 'Yes' : 'No'}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-muted-foreground">
                            {rule.actionTaken}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Approver Responses */}
              {stage.approverResponses && stage.approverResponses.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                    Approver Responses
                  </p>
                  <div className="space-y-2">
                    {stage.approverResponses.map((resp, i) => {
                      const decisionCfg = resp.decision === 'approved'
                        ? { label: 'Approved', className: 'bg-success-100 text-success-800' }
                        : { label: 'Rejected', className: 'bg-error-100 text-error-800' };
                      return (
                        <div key={i} className="rounded-md border p-3 space-y-1.5">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{resp.approverName}</span>
                              {resp.isDelegated && resp.delegationFrom && (
                                <span className="text-xs text-muted-foreground">
                                  (via delegation from {resp.delegationFrom})
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={cn('text-xs', decisionCfg.className)}>
                                {decisionCfg.label}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {format(resp.timestamp, 'dd MMM yyyy HH:mm')}
                              </span>
                            </div>
                          </div>
                          {resp.comments && (
                            <p className="text-xs text-muted-foreground italic">"{resp.comments}"</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
